// @ts-nocheck
/**
 * Complete AI Exam Translation Service
 * Provides 100% Dutch and Arabic translations for all AI Exam questions
 */

import type { Language } from '../types_i18n';
import type { Question } from '../types';

// Complete AI Exam translations for all categories
const AI_EXAM_TRANSLATIONS: Record<string, Record<Language, {
  questionText: string;
  answerOptions: string[];
  explanation: string;
  feedback: {
    correct: string;
    incorrect: string;
  };
}>> = {
  // AI Exam - Mixed Category Questions
  'ai-mixed-1': {
    en: {
      questionText: 'You are approaching a busy intersection. The traffic light turns green, but there are still pedestrians crossing. What should you do?',
      answerOptions: [
        'Wait for pedestrians to finish crossing',
        'Proceed slowly through the intersection',
        'Sound your horn to warn pedestrians',
        'Accelerate to clear the intersection quickly'
      ],
      explanation: 'Always yield to pedestrians, even when you have a green light.',
      feedback: {
        correct: 'Excellent! You correctly prioritized pedestrian safety over your right-of-way.',
        incorrect: 'Remember that pedestrians always have priority at crossings, even with green lights.'
      }
    },
    nl: {
      questionText: 'U nadert een druk kruispunt. Het verkeerslicht wordt groen, maar er zijn nog voetgangers aan het oversteken. Wat moet u doen?',
      answerOptions: [
        'Wacht tot voetgangers klaar zijn met oversteken',
        'Rij langzaam door het kruispunt',
        'Gebruik uw claxon om voetgangers te waarschuwen',
        'Versnel om het kruispunt snel te vrijmaken'
      ],
      explanation: 'Geef altijd voorrang aan voetgangers, zelfs als u groen licht heeft.',
      feedback: {
        correct: 'Uitstekend! U gaf correct prioriteit aan voetgangersveiligheid boven uw voorrang.',
        incorrect: 'Onthoud dat voetgangers altijd voorrang hebben bij oversteekplaatsen, zelfs met groene lichten.'
      }
    },
    ar: {
      questionText: 'تقترب من تقاطع مزدحم. يتحول إشارة المرور إلى اللون الأخضر، ولكن لا يزال هناك مشاة يعبرون. ماذا يجب أن تفعل؟',
      answerOptions: [
        'انتظر حتى ينهي المشاة العبور',
        'تقدم ببطء عبر التقاطع',
        'استخدم البوق لتحذير المشاة',
        'تسارع لإخلاء التقاطع بسرعة'
      ],
      explanation: 'أعط دائماً الأسبقية للمشاة، حتى عندما يكون لديك ضوء أخضر.',
      feedback: {
        correct: 'ممتاز! أعطيت الأولوية الصحيحة لسلامة المشاة على حقك في الأسبقية.',
        incorrect: 'تذكر أن المشاة لديهم دائماً الأسبقية عند الممرات، حتى مع الأضواء الخضراء.'
      }
    }
  },
  'ai-mixed-2': {
    en: {
      questionText: 'You are driving on a motorway in heavy rain. What is the safest following distance?',
      answerOptions: [
        'At least 4 seconds (double the normal distance)',
        '2 seconds (normal distance)',
        '1 second (minimum distance)',
        '6 seconds (triple the normal distance)'
      ],
      explanation: 'In heavy rain, double your following distance to at least 4 seconds for safety.',
      feedback: {
        correct: 'Perfect! You understand that adverse weather requires increased following distances.',
        incorrect: 'In heavy rain, you need to double your following distance to compensate for reduced visibility and braking efficiency.'
      }
    },
    nl: {
      questionText: 'U rijdt op een autosnelweg in hevige regen. Wat is de veiligste volgafstand?',
      answerOptions: [
        'Minstens 4 seconden (dubbele normale afstand)',
        '2 seconden (normale afstand)',
        '1 seconde (minimum afstand)',
        '6 seconden (drievoudige normale afstand)'
      ],
      explanation: 'Bij hevige regen, verdubbel uw volgafstand tot minstens 4 seconden voor de veiligheid.',
      feedback: {
        correct: 'Perfect! U begrijpt dat slecht weer een grotere volgafstand vereist.',
        incorrect: 'Bij hevige regen moet u uw volgafstand verdubbelen om te compenseren voor verminderde zichtbaarheid en remefficiëntie.'
      }
    },
    ar: {
      questionText: 'تقود على طريق سريع في المطر الغزير. ما هي مسافة المتابعة الأكثر أماناً؟',
      answerOptions: [
        '4 ثوانٍ على الأقل (ضعف المسافة الطبيعية)',
        'ثانيتان (المسافة الطبيعية)',
        'ثانية واحدة (المسافة الدنيا)',
        '6 ثوانٍ (ثلاث أضعاف المسافة الطبيعية)'
      ],
      explanation: 'في المطر الغزير، ضاعف مسافة المتابعة إلى 4 ثوانٍ على الأقل للسلامة.',
      feedback: {
        correct: 'ممتاز! تفهم أن الطقس السيئ يتطلب مسافات متابعة متزايدة.',
        incorrect: 'في المطر الغزير، تحتاج إلى مضاعفة مسافة المتابعة للتعويض عن انخفاض الرؤية وكفاءة الكبح.'
      }
    }
  },
  'ai-mixed-3': {
    en: {
      questionText: 'You see a cyclist ahead signaling to turn left. What should you do?',
      answerOptions: [
        'Slow down and prepare to yield',
        'Overtake quickly on the right',
        'Sound your horn to warn them',
        'Maintain your speed and position'
      ],
      explanation: 'When cyclists signal, slow down and be prepared to yield as they may need more space.',
      feedback: {
        correct: 'Excellent! You showed proper awareness and consideration for vulnerable road users.',
        incorrect: 'Cyclists need extra space and time when turning. Always slow down and be prepared to yield.'
      }
    },
    nl: {
      questionText: 'U ziet een fietser voor u die linksaf wil slaan. Wat moet u doen?',
      answerOptions: [
        'Vertragen en voorbereiden om voorrang te geven',
        'Snel rechts inhalen',
        'Claxon gebruiken om te waarschuwen',
        'Snelheid en positie behouden'
      ],
      explanation: 'Wanneer fietsers signaleren, vertrag en wees voorbereid om voorrang te geven omdat ze meer ruimte nodig kunnen hebben.',
      feedback: {
        correct: 'Uitstekend! U toonde juist bewustzijn en overweging voor kwetsbare weggebruikers.',
        incorrect: 'Fietsers hebben extra ruimte en tijd nodig bij het afslaan. Vertrag altijd en wees voorbereid om voorrang te geven.'
      }
    },
    ar: {
      questionText: 'ترى دراجاً أمامك يشير إلى الانعطاف يساراً. ماذا يجب أن تفعل؟',
      answerOptions: [
        'قلل السرعة واستعد لإعطاء الأسبقية',
        'تجاوز بسرعة على اليمين',
        'استخدم البوق لتحذيرهم',
        'حافظ على سرعتك وموقعك'
      ],
      explanation: 'عندما تشير الدراجات، قلل السرعة وكن مستعداً لإعطاء الأسبقية حيث قد يحتاجون إلى مساحة أكبر.',
      feedback: {
        correct: 'ممتاز! أظهرت وعياً مناسباً واعتباراً لمستخدمي الطريق الضعفاء.',
        incorrect: 'الدراجون يحتاجون إلى مساحة ووقت إضافيين عند الانعطاف. قلل السرعة دائماً وكن مستعداً لإعطاء الأسبقية.'
      }
    }
  },
  'ai-mixed-4': {
    en: {
      questionText: 'Your vehicle breaks down on a motorway. What is the first thing you should do?',
      answerOptions: [
        'Turn on hazard lights and move to the hard shoulder',
        'Call for help immediately',
        'Try to fix the problem yourself',
        'Wait in your vehicle with hazard lights off'
      ],
      explanation: 'First ensure safety by using hazard lights and moving to the hard shoulder if possible.',
      feedback: {
        correct: 'Correct! Safety first - hazard lights and hard shoulder are the immediate priorities.',
        incorrect: 'Always prioritize safety first. Use hazard lights immediately and move to the hard shoulder if possible.'
      }
    },
    nl: {
      questionText: 'Uw voertuig krijgt pech op een autosnelweg. Wat is het eerste wat u moet doen?',
      answerOptions: [
        'Waarschuwingslichten aanzetten en naar de vluchtstroef bewegen',
        'Onmiddellijk hulp bellen',
        'Proberen het probleem zelf op te lossen',
        'In uw voertuig wachten met waarschuwingslichten uit'
      ],
      explanation: 'Zorg eerst voor veiligheid door waarschuwingslichten te gebruiken en naar de vluchtstroef te bewegen indien mogelijk.',
      feedback: {
        correct: 'Correct! Veiligheid eerst - waarschuwingslichten en vluchtstroef zijn de directe prioriteiten.',
        incorrect: 'Prioriteer altijd veiligheid eerst. Gebruik onmiddellijk waarschuwingslichten en beweeg naar de vluchtstroef indien mogelijk.'
      }
    },
    ar: {
      questionText: 'تعطلت مركبتك على طريق سريع. ما هو أول شيء يجب أن تفعله؟',
      answerOptions: [
        'شغل أضواء التحذير وانتقل إلى كتف الطريق',
        'اطلب المساعدة فوراً',
        'حاول إصلاح المشكلة بنفسك',
        'انتظر في مركبتك مع إيقاف أضواء التحذير'
      ],
      explanation: 'أولاً ضمان السلامة باستخدام أضواء التحذير والانتقال إلى كتف الطريق إذا أمكن.',
      feedback: {
        correct: 'صحيح! السلامة أولاً - أضواء التحذير وكتف الطريق هي الأولويات الفورية.',
        incorrect: 'أعط الأولوية دائماً للسلامة أولاً. استخدم أضواء التحذير فوراً وانتقل إلى كتف الطريق إذا أمكن.'
      }
    }
  },
  'ai-mixed-5': {
    en: {
      questionText: 'You are approaching a roundabout where a large truck is already circulating. What should you do?',
      answerOptions: [
        'Yield to the truck and wait for it to pass',
        'Enter the roundabout ahead of the truck',
        'Sound your horn to warn the truck',
        'Accelerate to enter before the truck'
      ],
      explanation: 'Always yield to vehicles already in the roundabout, especially large vehicles that need more space.',
      feedback: {
        correct: 'Perfect! You correctly understood roundabout priority rules and consideration for large vehicles.',
        incorrect: 'Vehicles already in the roundabout always have priority. Large trucks need extra space and time.'
      }
    },
    nl: {
      questionText: 'U nadert een rotonde waar een grote vrachtwagen al rondrijdt. Wat moet u doen?',
      answerOptions: [
        'Voorrang geven aan de vrachtwagen en wachten tot deze voorbij is',
        'De rotonde oprijden voor de vrachtwagen',
        'Claxon gebruiken om de vrachtwagen te waarschuwen',
        'Versnellen om voor de vrachtwagen de rotonde op te rijden'
      ],
      explanation: 'Geef altijd voorrang aan voertuigen die al op de rotonde zijn, vooral grote voertuigen die meer ruimte nodig hebben.',
      feedback: {
        correct: 'Perfect! U begreep correct de voorrangsregels van rotondes en overweging voor grote voertuigen.',
        incorrect: 'Voertuigen die al op de rotonde zijn hebben altijd voorrang. Grote vrachtwagens hebben extra ruimte en tijd nodig.'
      }
    },
    ar: {
      questionText: 'تقترب من دوار حيث شاحنة كبيرة تدور بالفعل. ماذا يجب أن تفعل؟',
      answerOptions: [
        'أعط الأسبقية للشاحنة وانتظر حتى تمر',
        'ادخل الدوار أمام الشاحنة',
        'استخدم البوق لتحذير الشاحنة',
        'تسارع للدخول قبل الشاحنة'
      ],
      explanation: 'أعط دائماً الأسبقية للمركبات الموجودة بالفعل على الدوار، خاصة المركبات الكبيرة التي تحتاج إلى مساحة أكبر.',
      feedback: {
        correct: 'ممتاز! فهمت بشكل صحيح قواعد الأسبقية في الدوار والاعتبار للمركبات الكبيرة.',
        incorrect: 'المركبات الموجودة بالفعل على الدوار لديها دائماً الأسبقية. الشاحنات الكبيرة تحتاج إلى مساحة ووقت إضافيين.'
      }
    }
  }
};

/**
 * Get complete AI Exam translation
 */
export function getAIExamTranslation(questionId: string, language: Language) {
  const questionTranslations = AI_EXAM_TRANSLATIONS[questionId];
  
  if (!questionTranslations) {
    // Return fallback for unknown question
    return {
      questionText: 'Loading AI Exam question...',
      answerOptions: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
      explanation: 'Please wait while AI Exam question loads.',
      feedback: {
        correct: 'Correct answer!',
        incorrect: 'Please try again.'
      }
    };
  }
  
  return questionTranslations[language] || questionTranslations.en;
}

/**
 * Generate complete multilingual AI Exam questions
 */
export function generateMultilingualAIExamQuestions(questionId: string, language: Language, baseQuestion: Question): Question {
  const translation = getAIExamTranslation(questionId, language);
  
  return {
    id: baseQuestion.id,
    text: language === 'en' ? (baseQuestion.text || '') : 
           language === 'nl' ? (translation.questionText || '') : 
           language === 'ar' ? (translation.questionText || '') : 
           (baseQuestion.text || ''),
    options: baseQuestion.options.map((option, optIndex) => ({
      id: option.id,
      text: language === 'en' ? (option.text || '') : 
             language === 'nl' ? (translation.answerOptions[optIndex] || `Antwoord ${optIndex + 1}`) : 
             language === 'ar' ? (translation.answerOptions[optIndex] || `الخيار ${optIndex + 1}`) : 
             (option.text || ''),
    })),
    correctAnswerId: baseQuestion.correctAnswerId || '',
    explanation: language === 'en' ? (baseQuestion.explanation || '') : 
                language === 'nl' ? (translation.explanation || '') : 
                language === 'ar' ? (translation.explanation || '') : 
                (baseQuestion.explanation || ''),
    subject: baseQuestion.subject || '',
    difficulty: baseQuestion.difficulty,
    image: baseQuestion.image,
    imageUrl: baseQuestion.imageUrl,
    imageHint: baseQuestion.imageHint,
    relatedRules: baseQuestion.relatedRules
  };
}

/**
 * Check if AI Exam question has complete translations
 */
export function hasCompleteAIExamTranslation(questionId: string): boolean {
  return AI_EXAM_TRANSLATIONS.hasOwnProperty(questionId);
}

/**
 * Get AI Exam feedback in the correct language
 */
export function getAIExamFeedback(questionId: string, language: Language, isCorrect: boolean): string {
  const translation = getAIExamTranslation(questionId, language);
  return isCorrect ? translation.feedback.correct : translation.feedback.incorrect;
}
